import { useState, useEffect } from "react";
import { Search, Package } from "lucide-react";
import { Input } from "@/components/ui/input";
import { GlassCard } from "@/components/ui/glass-card";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { useApplications } from "@/hooks/useApplications";
import { PlatformIcons } from "@/components/profiles/PlatformIcons";
import { Platform } from "@/types/profile";

interface TagApplicationsTabProps {
  tagId: string;
}

export const TagApplicationsTab = ({ tagId }: TagApplicationsTabProps) => {
  const [assignedAppIds, setAssignedAppIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isFetching, setIsFetching] = useState(true);
  const { applications: allApps, loading: appsLoading } = useApplications();

  useEffect(() => {
    const fetchAssignedApps = async () => {
      try {
        setIsFetching(true);
        const { data, error } = await supabase
          .from('application_tags')
          .select('application_id')
          .eq('tag_id', tagId);
        
        if (error) {
          console.error('Error fetching application tags:', error);
          setAssignedAppIds([]);
        } else {
          setAssignedAppIds(data?.map(item => item.application_id) || []);
        }
      } catch (err) {
        console.error('Error:', err);
        setAssignedAppIds([]);
      } finally {
        setIsFetching(false);
      }
    };
    
    fetchAssignedApps();
  }, [tagId]);

  // Filter to only show apps that are in the database assignment
  const applications = allApps.filter(app => 
    assignedAppIds.includes(app.id)
  );

  const filteredApplications = applications.filter(app =>
    app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    app.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isLoading = appsLoading || isFetching;

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        {Array.from({ length: 12 }).map((_, i) => (
          <GlassCard key={i} variant="subtle" className="p-4 animate-pulse">
            <div className="flex flex-col items-center space-y-3">
              <div className="w-12 h-12 rounded-xl bg-muted"></div>
              <div className="space-y-1 w-full">
                <div className="h-4 bg-muted rounded w-3/4 mx-auto"></div>
                <div className="h-3 bg-muted rounded w-1/2 mx-auto"></div>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t space-y-2">
              <div className="h-2 bg-muted rounded"></div>
              <div className="h-2 bg-muted rounded w-2/3"></div>
            </div>
          </GlassCard>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search applications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
      </div>

      {filteredApplications.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Package className="h-16 w-16 text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium text-muted-foreground mb-2">No Applications Found</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            {searchQuery 
              ? "No applications match your search criteria. Try adjusting your search terms."
              : "This tag has no applications assigned to it yet. Applications will appear here when they are tagged."
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {filteredApplications.map((application) => (
            <GlassCard key={application.id} variant="subtle" className="p-4">
              <div className="flex flex-col items-center text-center space-y-3">
                <div className="w-12 h-12 rounded-xl overflow-hidden bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center border">
                  {application.icon ? (
                    <img 
                      src={application.icon} 
                      alt={application.name} 
                      className="w-8 h-8 object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  <Package className={`h-6 w-6 text-primary ${application.icon ? 'hidden' : ''}`} />
                </div>
                <div className="space-y-1">
                  <Link 
                    to={`/applications/${application.id}`}
                    className="font-medium text-sm line-clamp-2 min-h-[2.5rem] flex items-center hover:text-primary transition-colors"
                  >
                    {application.name}
                  </Link>
                  <p className="text-xs text-muted-foreground">{application.version}</p>
                </div>
              </div>
              
              <div className="mt-3 pt-3 border-t space-y-1">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted-foreground">Platform</span>
                  <PlatformIcons platforms={[application.platform as Platform]} size="sm" />
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted-foreground">Source</span>
                  <span className="font-medium capitalize">{application.source}</span>
                </div>
              </div>
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  );
};